import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * 🔴 PH-52 — "are the closed sprints on screen?", in the URL.
 *
 *     /backlog?project=1                → closed sprints hidden (the default, and the ticket)
 *     /backlog?project=1&closed=show    → revealed, because somebody asked
 *
 * ⭐⭐ WHY THE URL AND NOT localStorage, WHICH IS THE OBVIOUS CHOICE. The two directions of this
 * toggle are NOT symmetric, so a single device-sticky boolean is the wrong shape for it:
 *
 *   · HIDDEN is the default and needs no memory.
 *   · SHOWN is an act of INSPECTION — "what did we ship?" — and it is finished a minute later.
 *
 * A `localStorage` flag would make one such peek permanent, on that device, invisibly, and the board
 * would quietly grow back into the state Or reported, weeks later, with nobody having chosen it.
 * ⛔ That is a one-way ratchet back to the defect. The theme is the one preference this app does keep
 * in `localStorage`, and it is genuinely a device preference that cannot re-create a data-volume
 * problem.
 *
 * ⭐ THE URL IS NOT "NO PERSISTENCE" — it is persistence with the right SCOPE, and it is the rule
 * `useSectionCollapse` already follows for `opened`/`collapsed`. A reveal survives a reload (the URL
 * is the address), a filter change (`nextFilterParams` preserves everything it does not name),
 * opening a ticket and pressing Back (both write `replace`-style params, never a fresh URL) — the
 * whole working session on this screen. What it does NOT survive is navigating to the backlog afresh,
 * where the default is supposed to reassert. And it is shareable: "look at what we shipped" is a
 * link.
 *
 * ⚠️ ONLY the exact value `show` reveals. `?closed=1`, `?closed=true` and `?closed=banana` all mean
 * hidden — the same "ignore rather than trust user-editable text" rule as `isValidSectionKey`, and
 * here the fallback is deliberately the SAFE direction.
 *
 * ⭐ `replace: true`: revealing a section is a change of VIEW, not of place. Pushing history would
 * make Back toggle the closed sprints instead of leaving the backlog.
 */

export const CLOSED_SPRINTS_PARAM = 'closed';
export const CLOSED_SPRINTS_SHOWN = 'show';

export function closedSprintsShown(params: URLSearchParams): boolean {
  return params.get(CLOSED_SPRINTS_PARAM) === CLOSED_SPRINTS_SHOWN;
}

/**
 * The URL after the toggle is set.
 *
 * ⭐ Hiding DELETES the parameter rather than writing `closed=hide`: the default must have one
 * spelling, or `?closed=hide` and no parameter become two URLs for the same view and every link
 * carries a decision nobody made.
 */
export function writeClosedSprints(params: URLSearchParams, show: boolean): URLSearchParams {
  const next = new URLSearchParams(params);
  if (show) next.set(CLOSED_SPRINTS_PARAM, CLOSED_SPRINTS_SHOWN);
  else next.delete(CLOSED_SPRINTS_PARAM);
  return next;
}

export interface ClosedSprints {
  show: boolean;
  setShow: (show: boolean) => void;
}

export function useClosedSprints(): ClosedSprints {
  const [params, setParams] = useSearchParams();

  const show = useMemo(() => closedSprintsShown(params), [params]);

  const setShow = useCallback(
    (next: boolean) => {
      setParams(writeClosedSprints(params, next), { replace: true });
    },
    [params, setParams],
  );

  return { show, setShow };
}
